<script setup>
import { computed } from "vue";
import { useField } from "vee-validate";
import { ErrorMessage } from ".";
import { validationModes } from "./validation-modes";

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  optionLabel: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  autocomplete: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "text",
  },
  disabled: {
    type: Boolean,
  },
  validationMode: {
    type: String,
    default: "eager",
  },
});

const {
  value: fieldValue,
  errorMessage,
  meta,
  handleChange,
  handleBlur,
} = useField(
  props.name,
  {},
  {
    validateOnValueUpdate: true,
  }
);

/**
 * The dropdown reads straight off the field rather than mirroring it into a ref of its own: the
 * field is the source of truth, and it is written from outside the widget every time a form is
 * filled from loaded data. A copy kept in step by a pair of watchers cannot do that - each one
 * feeds the other, and the write-back guard that stopped the loop also refused every option that
 * was not a string, which is all of them wherever the label and the stored id differ.
 *
 * `value` is the whole chosen OPTION, and the rest of the field is carried through: the change
 * handler writes the event alongside it.
 */
const model = computed({
  get: () => fieldValue.value?.value ?? null,
  set: (option) => {
    fieldValue.value = { ...fieldValue.value, value: option };
  },
});

const handlers = computed(() => {
  const on = {
    blur: handleBlur,
    input: [(e) => handleChange(e, false)],
    change: undefined,
  };

  const triggers = validationModes[props.validationMode]({
    errorMessage,
    meta,
  });

  triggers.forEach((trigger) => {
    if (Array.isArray(on[trigger])) {
      on[trigger].push(handleChange);
    } else {
      on[trigger] = handleChange;
    }
  });

  return on;
});
</script>

<template>
  <div>
    <FormInputLabel v-if="props.label?.length" :for="props.name">
      {{ props.label }}
    </FormInputLabel>
    <div class="relative">
      <Dropdown
        v-model="model"
        :name="props.name"
        :input-id="props.name"
        :option-label="props.optionLabel"
        :options="props.options"
        :placeholder="props.placeholder"
        :autocomplete="props.autocomplete"
        :disabled="props.disabled"
        :class="[errorMessage?.length && 'error-state', 'dropdown']"
        v-on="handlers"
      />
    </div>
    <ErrorMessage :message="errorMessage" />
  </div>
</template>

<style scoped>
.dropdown {
  width: 100%;
}
.error-state {
  border-color: red;
  &:hover {
    border-color: red;
  }
}
</style>
